import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Settings, Eye, Trash2, Calendar, CreditCard, AlertCircle, Globe, Send } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useSite } from '../context/SiteContext';
import DomainManager from './DomainManager';
import SubscriptionManager from './SubscriptionManager';
import SiteTransferModal from './SiteTransferModal';
import { getPreviewUrl } from '../utils/siteUrls';
import toast from 'react-hot-toast';

const Dashboard = () => {
  const { user, logout } = useAuth();
  const { sites, loading, fetchSites, deleteSite } = useSite();
  const [planInfo, setPlanInfo] = useState(null);
  const [domainManagerOpen, setDomainManagerOpen] = useState(false);
  const [selectedSite, setSelectedSite] = useState(null);
  const [subscriptionManagerOpen, setSubscriptionManagerOpen] = useState(false);
  const [transferModalOpen, setTransferModalOpen] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        const response = await fetch('/api/sites', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.planInfo) {
            setPlanInfo(data.planInfo);
          }
        }
      } catch (error) {
        console.error('Failed to load plan info:', error);
      }
      
      await fetchSites();
    };
    
    loadData();
  }, [fetchSites]);

  const handleDeleteSite = async (siteId, siteName) => {
    if (window.confirm(`Are you sure you want to delete "${siteName}"? This action cannot be undone.`)) {
      try {
        await deleteSite(siteId);
        toast.success('Site deleted successfully');
      } catch (error) {
        toast.error('Failed to delete site');
      }
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const openDomainManager = (site) => {
    setSelectedSite(site);
    setDomainManagerOpen(true);
  };

  const closeDomainManager = () => {
    setDomainManagerOpen(false);
    setSelectedSite(null);
  };

  const openTransferModal = (site) => {
    setSelectedSite(site);
    setTransferModalOpen(true);
  };

  const closeTransferModal = () => {
    setTransferModalOpen(false);
    setSelectedSite(null);
  };

  const handleTransferComplete = () => {
    // Refresh sites list after successful transfer
    fetchSites();
  };

  return (
    <div className="min-h-screen bg-gray-800">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">WebC Builder</h1>
              <p className="text-gray-600">Welcome back, {user?.name}</p>
            </div>
            <div className="flex items-center space-x-4">
              {planInfo && (
                <button
                  onClick={() => setSubscriptionManagerOpen(true)}
                  className="flex items-center space-x-2 px-3 py-1 bg-blue-50 rounded-lg hover:bg-blue-100 transition"
                >
                  <CreditCard className="w-4 h-4 text-blue-600" />
                  <span className="text-sm font-medium text-blue-800">{planInfo.plan.name}</span>
                  <span className="text-xs text-blue-600">
                    {planInfo.limits.sites.current}/{planInfo.limits.sites.max} sites
                  </span>
                </button>
              )}
              {user?.role === 'admin' && (
                <Link
                  to="/admin"
                  className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition flex items-center"
                >
                  <Settings className="w-4 h-4 mr-1" />
                  Admin
                </Link>
              )}
              <button
                onClick={logout}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 transition"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        {/* Plan Usage Section */}
        {planInfo && (
          <div className="bg-white rounded-lg shadow-sm border p-6 mb-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Plan Usage</h3>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                planInfo.plan.slug === 'free' ? 'bg-gray-100 text-gray-800' :
                planInfo.plan.slug === 'basic' ? 'bg-green-100 text-green-800' :
                planInfo.plan.slug === 'pro' ? 'bg-blue-100 text-blue-800' :
                'bg-purple-100 text-purple-800'
              }`}>
                {planInfo.plan.name} Plan
              </span>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">Sites</span>
                  {planInfo.limits.sites.remaining === 0 && planInfo.limits.sites.max !== 'Unlimited' && (
                    <AlertCircle className="w-4 h-4 text-red-500" />
                  )}
                </div>
                <div className="text-2xl font-bold text-gray-900">
                  {planInfo.limits.sites.current}
                  <span className="text-sm font-normal text-gray-500">/{planInfo.limits.sites.max}</span>
                </div>
                {planInfo.limits.sites.max !== 'Unlimited' && (
                  <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                    <div 
                      className={`h-2 rounded-full ${
                        planInfo.limits.sites.remaining === 0 ? 'bg-red-500' : 'bg-blue-500'
                      }`}
                      style={{
                        width: `${Math.min((planInfo.limits.sites.current / planInfo.limits.sites.max) * 100, 100)}%`
                      }}
                    ></div>
                  </div>
                )}
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="text-sm font-medium text-gray-700 mb-2">Pages per Site</div>
                <div className="text-2xl font-bold text-gray-900">
                  {planInfo.limits.pages.max}
                </div>
                <div className="text-xs text-gray-500 mt-1">Maximum allowed</div>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="text-sm font-medium text-gray-700 mb-2">Sections per Page</div>
                <div className="text-2xl font-bold text-gray-900">
                  {planInfo.limits.sections.max}
                </div>
                <div className="text-xs text-gray-500 mt-1">Maximum allowed</div>
              </div>
            </div>
            
            {planInfo.limits.sites.remaining === 0 && planInfo.limits.sites.max !== 'Unlimited' && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center">
                  <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
                  <span className="text-sm text-red-800">
                    You've reached your site limit. Upgrade your plan to create more sites.
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="flex justify-between items-center mb-8">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Your Websites</h2>
            <p className="text-gray-600">Manage and edit your created websites</p>
          </div>
          {planInfo && planInfo.limits.sites.remaining > 0 ? (
            <Link
              to="/create"
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition shadow-sm"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create New Site
            </Link>
          ) : planInfo && planInfo.limits.sites.max !== 'Unlimited' ? (
            <div className="inline-flex items-center px-4 py-2 bg-gray-300 text-gray-500 rounded-lg cursor-not-allowed">
              <Plus className="w-4 h-4 mr-2" />
              Site Limit Reached
            </div>
          ) : (
            <Link
              to="/create"
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition shadow-sm"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create New Site
            </Link>
          )}
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading your sites...</p>
          </div>
        ) : !sites || sites.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow-sm">
            <div className="max-w-md mx-auto">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Plus className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No websites yet</h3>
              <p className="text-gray-600 mb-6">Get started by creating your first website with our AI-powered builder.</p>
              <Link
                to="/create"
                className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Your First Site
              </Link>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {(sites || []).map((site) => (
              <div key={site._id} className="bg-white rounded-lg shadow-sm border hover:shadow-md transition-shadow">
                <div className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 
                        className="text-lg font-semibold text-gray-900 mb-1"
                        onClick={() => console.log('🏷️ Dashboard site:', { id: site._id, name: site.name, businessName: site.businessInfo?.name })}
                      >
                        {site.name}
                      </h3>
                      <p className="text-sm text-gray-600">{site.businessInfo?.description?.substring(0, 80)}...</p>
                    </div>
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      site.status === 'published' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {site.status}
                    </span>
                  </div>
                  
                  <div className="flex items-center text-sm text-gray-500 mb-4">
                    <Calendar className="w-4 h-4 mr-1" />
                    Updated {formatDate(site.updatedAt)}
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex space-x-2">
                      <Link
                        to={`/editor/${site._id}`}
                        onClick={() => console.log('🖱️ Clicked Edit for site:', { id: site._id, name: site.name, businessName: site.businessInfo?.name })}
                        className="inline-flex items-center px-3 py-1.5 text-sm bg-blue-50 text-blue-600 rounded hover:bg-blue-100 transition"
                      >
                        <Settings className="w-3 h-3 mr-1" />
                        Edit
                      </Link>
                      {site.status === 'published' && site.domain && (
                        <a
                          href={getPreviewUrl(site.domain)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center px-3 py-1.5 text-sm bg-green-50 text-green-600 rounded hover:bg-green-100 transition"
                        >
                          <Eye className="w-3 h-3 mr-1" />
                          View
                        </a>
                      )}
                      {site.status === 'published' && (
                        <button
                          onClick={() => openDomainManager(site)}
                          className="inline-flex items-center px-3 py-1.5 text-sm bg-purple-50 text-purple-600 rounded hover:bg-purple-100 transition"
                        >
                          <Globe className="w-3 h-3 mr-1" />
                          Domains
                        </button>
                      )}
                      <button
                        onClick={() => openTransferModal(site)}
                        className="inline-flex items-center px-3 py-1.5 text-sm bg-orange-50 text-orange-600 rounded hover:bg-orange-100 transition"
                        title="Transfer site to another user"
                      >
                        <Send className="w-3 h-3 mr-1" />
                        Transfer
                      </button>
                    </div>
                    <button
                      onClick={() => handleDeleteSite(site._id, site.name)}
                      className="p-1.5 text-gray-400 hover:text-red-600 transition"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Domain Manager Modal */}
      {domainManagerOpen && selectedSite && (
        <DomainManager
          siteId={selectedSite._id}
          siteName={selectedSite.name}
          onClose={closeDomainManager}
        />
      )}

      {/* Subscription Manager Modal */}
      {subscriptionManagerOpen && (
        <SubscriptionManager
          onClose={() => setSubscriptionManagerOpen(false)}
        />
      )}

      {/* Site Transfer Modal */}
      {transferModalOpen && selectedSite && (
        <SiteTransferModal
          site={selectedSite}
          isOpen={transferModalOpen}
          onClose={closeTransferModal}
          onTransferComplete={handleTransferComplete}
        />
      )}
    </div>
  );
};

export default Dashboard;